import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import TopNav from "@/components/TopNav";
import TagsMenuBar from "@/components/TagsMenuBar";
import { getWidgetPage, recordUnsubscribe } from "@/lib/api";

// Renders the Airtable widget HTML faithfully (its own <style>/<script>) inside
// a sandboxed iframe that auto-resizes to its content height, framed by the
// site header/nav (top) and the global SiteFooter (bottom).
// Widget HTML with in-page anchors (#module) is split into "pages": everything before
// the first anchor is the always-visible header (hero + module buttons); each anchor
// starts a new page and only the active page is shown. Buttons switch pages and
// scroll the parent window back to the top of the frame.
const ANCHOR_BRIDGE = `<style>html,body{overflow:hidden!important;scrollbar-width:none}::-webkit-scrollbar{display:none}</style><script>(function(){
  var pages={}, order=[], active=null;
  function targets(){var s={};var as=document.querySelectorAll('a[href^="#"]');for(var i=0;i<as.length;i++){var id=decodeURIComponent(as[i].getAttribute('href').slice(1));if(id)s[id]=1;}
    var os=document.querySelectorAll('[onclick*="getElementById"]');for(var j=0;j<os.length;j++){var m=/getElementById\\(['"]([^'"]+)['"]\\)/.exec(os[j].getAttribute('onclick')||'');if(m)s[m[1]]=1;}return s;}
  function build(){
    if(document.body.hasAttribute('data-no-pager'))return;
    var ids=targets(), anchors=[];
    for(var id in ids){var el=document.getElementById(id);if(el)anchors.push(el);}
    if(anchors.length<2)return;
    var root=anchors[0].parentNode;
    for(var a=1;a<anchors.length;a++){if(anchors[a].parentNode!==root)return;}
    var kids=Array.prototype.slice.call(root.children), cur=null;
    for(var i=0;i<kids.length;i++){var k=kids[i];if(k.tagName==='SCRIPT')continue;
      if(k.id&&ids[k.id]){cur=document.createElement('section');cur.setAttribute('data-page',k.id);root.insertBefore(cur,k);pages[k.id]=cur;order.push(k.id);}
      if(cur){cur.appendChild(k);}}
    var h=parent.location.hash?decodeURIComponent(parent.location.hash.slice(1)):'';
    show(pages[h]?h:order[0], false);
  }
  function show(id, scroll){if(!pages[id])return;active=id;
    for(var k in pages){pages[k].style.display=(k===id)?'':'none';}
    var links=document.querySelectorAll('a[href^="#"]');for(var i=0;i<links.length;i++){var on=links[i].getAttribute('href')==='#'+id;links[i].setAttribute('aria-current',on?'page':'false');
      var bg=(links[i].style.background||links[i].style.backgroundColor||'').replace(/\\s/g,'').toLowerCase();
      if(bg==='#176278'||bg==='#0f1f2e'||bg==='rgb(23,98,120)'||bg==='rgb(15,31,46)'){links[i].style.background=on?'#176278':'#0f1f2e';}}
    parent.postMessage({type:'widget-page',id:id,scroll:!!scroll},'*');
    try{parent.history.replaceState(null,'',parent.location.pathname+parent.location.search+'#'+id);}catch(_){}}
  document.addEventListener('click',function(e){
    var oc=e.target.closest&&e.target.closest('[onclick*="getElementById"]');
    if(oc){var m=/getElementById\\(['"]([^'"]+)['"]\\)/.exec(oc.getAttribute('onclick')||'');if(m&&pages[m[1]]){e.preventDefault();e.stopPropagation();show(m[1],true);return;}}
    var ext=e.target.closest&&e.target.closest('a[href]:not([href^="#"])');
    if(ext){var hr=ext.getAttribute('href')||'';if(/^(javascript|mailto|tel):/i.test(hr))return;e.preventDefault();e.stopPropagation();parent.postMessage({type:'widget-nav',href:ext.href,target:ext.getAttribute('target')||''},'*');return;}
    var a=e.target.closest&&e.target.closest('a[href^="#"]');if(!a)return;
    var id=decodeURIComponent(a.getAttribute('href').slice(1));if(!id)return;e.preventDefault();
    if(pages[id]){show(id,true);return;}
    var el=document.getElementById(id);if(el){parent.postMessage({type:'widget-anchor',top:el.getBoundingClientRect().top+(window.pageYOffset||0)},'*');}},true);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',build);else build();
})();</script>`;

function HtmlFrame({ html, title }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(600);


  const resize = () => {
    try {
      const doc = ref.current?.contentWindow?.document;
      const body = doc?.body;
      if (body) {
        // body.offsetHeight follows the content (scrollHeight never shrinks below the
        // frame height, which left white space after switching to a shorter page).
        const cs = doc.defaultView.getComputedStyle(body);
        const h = Math.ceil(body.offsetHeight + parseFloat(cs.marginTop || 0) + parseFloat(cs.marginBottom || 0));
        if (h > 0) setHeight(h);
      }
    } catch {
      /* cross-origin guard */
    }
  };

  useEffect(() => {
    const onMsg = (e) => {
      if (e.source !== ref.current?.contentWindow) return;
      const frameTop = ref.current.getBoundingClientRect().top + window.pageYOffset;
      if (e.data?.type === "widget-page") {
        setTimeout(resize, 50);
        setTimeout(resize, 400);
        setTimeout(resize, 1200);
        if (e.data.scroll) window.scrollTo({ top: Math.max(0, frameTop - 84), behavior: "smooth" });
      } else if (e.data?.type === "widget-anchor") {
        window.scrollTo({ top: Math.max(0, frameTop + Number(e.data.top || 0) - 84), behavior: "smooth" });
      } else if (e.data?.type === "widget-nav" && e.data.href) {
        // Sandboxed frame can't navigate the top window itself. Shop links
        // (orevital.com or this site) open on the current domain; others in a new tab.
        try {
          const u = new URL(e.data.href, window.location.origin);
          const host = u.hostname.replace(/^www\./, "");
          const isSite = host === "orevital.com" || u.origin === window.location.origin;
          if (isSite && e.data.target !== "_blank") {
            window.location.assign(u.pathname + u.search + u.hash);
          } else {
            window.open(u.href, "_blank", "noopener");
          }
        } catch {
          /* ignore bad href */
        }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    const id = setInterval(resize, 600);
    const stop = setTimeout(() => clearInterval(id), 6000);
    window.addEventListener("resize", resize);
    return () => {
      clearInterval(id);
      clearTimeout(stop);
      window.removeEventListener("resize", resize);
    };
  }, [html]);

  return (
    <iframe
      ref={ref}
      title={title || "page"}
      srcDoc={ANCHOR_BRIDGE + (html || "")}
      onLoad={resize}
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      className="w-full border-0 block overflow-hidden"
      scrolling="no"
      style={{ height }}
      data-testid="widget-page-frame"
    />
  );
}

export default function PageView() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Unsubscribe page: DO NOT opt out automatically (email scanners pre-fetch
  // links). Only record the opt-out when the recipient clicks the confirm
  // button below.
  const isUnsub = (slug || "").toLowerCase() === "unsubscribe";
  const unsubEmail = searchParams.get("email") || "";
  const [unsubBusy, setUnsubBusy] = useState(false);
  const [unsubDone, setUnsubDone] = useState(false);
  const [unsubErr, setUnsubErr] = useState("");

  const confirmUnsubscribe = async () => {
    if (!unsubEmail || unsubBusy) return;
    setUnsubBusy(true);
    setUnsubErr("");
    try {
      const res = await recordUnsubscribe(unsubEmail);
      if (res && res.matched > 0) {
        setUnsubDone(true);
      } else {
        // Endpoint worked but no account matched this email address.
        setUnsubErr(
          `We couldn't find an account for ${unsubEmail}. You won't receive further emails, but if this looks wrong, contact ms@orevital.com.`
        );
        setUnsubDone(true);
      }
    } catch (e) {
      setUnsubErr("Something went wrong. Please try again, or email ms@orevital.com to be removed.");
    } finally {
      setUnsubBusy(false);
    }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    setPage(null);
    window.scrollTo(0, 0);
    getWidgetPage(slug)
      .then((res) => alive && setPage(res))
      .catch((e) =>
        alive &&
        setError(e?.response?.status === 404 ? "Page not found." : "Could not load this page.")
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  // Resolve the unsubscribe page's in-content confirm button. The CMS HTML
  // ships an anchor with href="{{CONFIRM_UNSUBSCRIBE_URL}}" — a token that must
  // be rewritten to a working URL, else clicking it lands on a broken page.
  const rawHtml = page?.html || "";
  const hasCmsUnsubBtn = rawHtml.includes("{{CONFIRM_UNSUBSCRIBE_URL}}");
  const confirmUrl = `${process.env.REACT_APP_BACKEND_URL}/api/postcue/unsubscribe/confirm?email=${encodeURIComponent(unsubEmail)}`;
  const keepUrl = typeof window !== "undefined" ? window.location.origin : "https://orevital.com";
  const pageHtml = rawHtml
    .replace(/\{\{\s*CONFIRM_UNSUBSCRIBE_URL\s*\}\}/g, confirmUrl)
    .replace(/\{\{\s*KEEP_ACCOUNT_URL\s*\}\}/g, keepUrl)
    .replace(/<p>\s*`{2,}\s*<\/p>/g, "") // strip stray markdown code fences
    .replace(/`{3,}/g, "");

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="widget-page">
      <TopNav variant="auth" />
      <TagsMenuBar />
      <div className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="py-24 flex flex-col items-center text-zinc-400">
            <Loader2 className="animate-spin mb-2" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : error ? (
          <div
            className="rounded-xl border border-zinc-200 bg-zinc-50 p-8 text-center text-zinc-600"
            data-testid="widget-page-error"
          >
            {error}
          </div>
        ) : (
          <div className="rounded-2xl border border-zinc-200 shadow-sm overflow-hidden bg-white">
            {page.badge_header && (
              <div className="px-5 pt-5">
                <span
                  className="inline-block rounded-lg bg-slate-700 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5"
                  data-testid="widget-page-badge"
                >
                  {page.badge_header}
                </span>
              </div>
            )}
            {page.type === "text_image" && page.image_url && !/<\s*img/i.test(page.html || "") && (
              <img
                src={page.image_url}
                alt={page.page}
                className="w-full object-cover mt-4"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
            <div className="p-1 sm:p-2">
              <HtmlFrame html={pageHtml} title={page.page} />
            </div>
          </div>
        )}

        {isUnsub && unsubEmail && !hasCmsUnsubBtn && (
          <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-6 text-center" data-testid="unsub-confirm-box">
            {unsubDone ? (
              <div data-testid="unsub-confirmed">
                {unsubErr ? (
                  <div className="text-amber-700 font-medium" data-testid="unsub-warn">{unsubErr}</div>
                ) : (
                  <div className="text-emerald-700 font-semibold">
                    You've been unsubscribed. You will no longer receive marketing emails at {unsubEmail}.
                  </div>
                )}
                <a
                  href="https://orevital.com"
                  className="inline-flex items-center justify-center mt-4 rounded-lg bg-[#0b2545] hover:bg-[#123a6b] text-white font-semibold px-6 py-3 transition-colors"
                  data-testid="unsub-return-btn"
                >
                  Return to OreVital.com
                </a>
              </div>
            ) : (
              <>
                <p className="text-zinc-700 mb-4">
                  Still want to stop these emails? Confirm below.
                </p>
                <button
                  type="button"
                  onClick={confirmUnsubscribe}
                  disabled={unsubBusy}
                  className="inline-flex items-center justify-center rounded-lg bg-[#0b2545] hover:bg-[#123a6b] disabled:opacity-60 text-white font-semibold px-6 py-3 transition-colors"
                  data-testid="unsub-confirm-btn"
                >
                  {unsubBusy ? "Unsubscribing…" : "Yes, unsubscribe me"}
                </button>
                {unsubErr && <p className="text-sm text-rose-600 mt-3" data-testid="unsub-error">{unsubErr}</p>}
                <p className="text-xs text-zinc-400 mt-3">
                  Changed your mind? Just close this page — nothing happens unless you click the button.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
